import type { PonudbaKategorija } from "@/mocks/ponudbaMeni";

interface MenuAccordionCardProps {
  kategorija: PonudbaKategorija;
  odprto: boolean;
  onToggle: (id: string) => void;
}

function steviloPostavk(stevilo: number) {
  if (stevilo === 1) return "1 postavka";
  if (stevilo === 2) return "2 postavki";
  if (stevilo === 3 || stevilo === 4) return `${stevilo} postavke`;
  return `${stevilo} postavk`;
}

export default function MenuAccordionCard({
  kategorija,
  odprto,
  onToggle,
}: MenuAccordionCardProps) {
  const preklopi = () => onToggle(kategorija.id);

  return (
    <article
      id={kategorija.id}
      className={`group relative scroll-mt-[150px] overflow-hidden rounded-lg border bg-background-50 transition-colors duration-500 md:scroll-mt-[162px] ${
        odprto
          ? "border-accent-400/70 bg-background-100/40"
          : "border-background-200 hover:border-accent-400/60 hover:bg-background-100/30"
      }`}
    >
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 left-0 w-[2px] origin-top bg-accent-500 transition-transform duration-500 ${
          odprto ? "scale-y-100" : "scale-y-0"
        }`}
      />

      <button
        type="button"
        onClick={preklopi}
        aria-expanded={odprto}
        aria-controls={`seznam-${kategorija.id}`}
        className="flex w-full cursor-pointer items-center gap-4 px-4 py-4 text-left md:px-5 md:py-5"
      >
        <span
          className={`relative h-[52px] w-[52px] shrink-0 overflow-hidden rounded-full border bg-background-100 transition-all duration-500 md:h-[58px] md:w-[58px] ${
            odprto
              ? "border-accent-400 bg-accent-100/60"
              : "border-background-200 group-hover:border-accent-300 group-hover:bg-accent-50"
          }`}
        >
          <img
            src={kategorija.slika}
            alt={`Ilustracija – ${kategorija.naslov}`}
            title={`${kategorija.naslov} – Bar Stanka Ptuj`}
            className="h-full w-full object-cover object-center transition-transform duration-700 ease-smooth group-hover:scale-[1.08]"
          />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="block font-label text-[9px] font-semibold uppercase tracking-[0.2em] text-accent-700">
              {kategorija.stevilka}
            </span>
            <span
              className={`h-px bg-accent-500/50 transition-all duration-500 ${
                odprto ? "w-6" : "w-3 group-hover:w-5"
              }`}
            />
          </span>
          <span className="mt-1 block font-heading text-[17px] leading-tight text-foreground-950 md:text-[19px]">
            {kategorija.naslov}
          </span>
          <span className="mt-1 block font-label text-[9px] uppercase tracking-[0.14em] text-foreground-400 sm:hidden">
            {steviloPostavk(kategorija.postavke.length)}
          </span>
        </span>

        <span className="hidden shrink-0 font-label text-[9px] uppercase tracking-[0.12em] text-foreground-400 sm:block">
          {steviloPostavk(kategorija.postavke.length)}
        </span>

        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-all duration-500 ${
            odprto
              ? "rotate-180 border-secondary-500 bg-secondary-500 text-background-50"
              : "border-background-300 text-foreground-500 group-hover:border-accent-400 group-hover:text-accent-700"
          }`}
        >
          <i className={`${odprto ? "ri-subtract-line" : "ri-add-line"} text-sm`} />
        </span>
      </button>

      <div
        id={`seznam-${kategorija.id}`}
        className={`grid transition-all duration-500 ease-smooth ${
          odprto ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <ul
            className={`border-t border-background-200/80 px-4 pb-4 pt-1 md:px-5 md:pb-5 ${
              odprto ? "is-open" : ""
            }`}
          >
            {kategorija.postavke.map((postavka, index) => (
              <li
                key={`${kategorija.id}-${postavka.ime}-${postavka.kolicina ?? "osnovno"}`}
                className="menu-item flex items-baseline justify-between gap-3 border-b border-background-200/60 py-[9px] last:border-b-0"
                style={{ transitionDelay: `${Math.min(index, 12) * 22}ms` }}
              >
                <span className="text-[13px] leading-snug text-foreground-800">
                  {postavka.ime}
                </span>
                {postavka.kolicina ? (
                  <span className="shrink-0 whitespace-nowrap font-label text-[11px] font-medium tracking-[0.05em] text-accent-700">
                    {postavka.kolicina}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </article>
  );
}